import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { useOralExamLabOverrides } from '@/common/oralExam/oralExamLabOverrides';

/** Last handshake canary verdict (hand-typed; not Orval-generated). */
export type HandshakeCanaryStatus = {
  verdict: string | null;
  classifiedAt: string | null;
  stale: boolean;
  failureStage: string;
  fixCommand: string;
  /** When true, 1:N Start fail-closes on an unhealthy canary. Defaults true (fail closed). */
  gatesStart?: boolean;
  probeInFlight?: boolean;
};

const HANDSHAKE_CANARY_PATH = '/handshake-canary';

export function getHandshakeCanaryQueryKey() {
  return ['oralExamHandshakeCanary'] as const;
}

export function isHandshakeCanaryHealthy(status: HandshakeCanaryStatus | undefined): boolean {
  if (!status) return false;
  return status.verdict === 'healthy' && !status.stale && !status.probeInFlight;
}

export function isHandshakeCanaryChecking(
  status: HandshakeCanaryStatus | undefined,
  options: { isFetched?: boolean; isError?: boolean } = {},
): boolean {
  const { isFetched = false, isError = false } = options;
  if (isError) return false;
  if (!isFetched) return true;
  if (!status) return true;
  if (status.probeInFlight) return true;
  if (status.verdict === null) return true;
  if (status.verdict === 'healthy' && status.stale) return true;
  return false;
}

export function isHandshakeCanaryUnavailable(
  status: HandshakeCanaryStatus | undefined,
  options: { isFetched?: boolean; isError?: boolean } = {},
): boolean {
  const { isFetched = false, isError = false } = options;
  if (isError) return true;
  if (isHandshakeCanaryChecking(status, { isFetched, isError })) return false;
  if (!isFetched) return false;
  if (!status?.verdict) return false;
  return status.verdict !== 'healthy';
}

export function getHandshakeCanaryRefetchIntervalMs(
  status: HandshakeCanaryStatus | undefined,
  options: { enabled?: boolean; isError?: boolean; isFetched?: boolean } = {},
): number | false {
  const { enabled = true, isError = false, isFetched = true } = options;
  if (!enabled || isError) return false;
  if (isHandshakeCanaryHealthy(status)) return false;
  if (isHandshakeCanaryChecking(status, { isFetched, isError })) return 2000;
  return false;
}

function labHealthyHandshakeCanaryStatus(): HandshakeCanaryStatus {
  return {
    verdict: 'healthy',
    classifiedAt: new Date().toISOString(),
    stale: false,
    failureStage: 'handshake_canary',
    fixCommand: 'yarn oral:env-preflight',
    gatesStart: true,
    probeInFlight: false,
  };
}

function emptyHandshakeCanaryStatus(): HandshakeCanaryStatus {
  return {
    verdict: null,
    classifiedAt: null,
    stale: true,
    failureStage: 'handshake_canary',
    fixCommand: 'yarn oral:env-preflight',
    gatesStart: true,
    probeInFlight: false,
  };
}

function mapHandshakeCanaryPayload(payload: Partial<HandshakeCanaryStatus> & { hop?: string } | undefined): HandshakeCanaryStatus {
  if (!payload) {
    return emptyHandshakeCanaryStatus();
  }
  return {
    verdict: payload.verdict ?? null,
    classifiedAt: payload.classifiedAt ?? null,
    stale: payload.stale ?? true,
    failureStage: payload.failureStage ?? payload.hop ?? 'handshake_canary',
    fixCommand: payload.fixCommand ?? 'yarn oral:env-preflight',
    gatesStart: payload.gatesStart !== false,
    probeInFlight: payload.probeInFlight === true,
  };
}

/**
 * Lecturer-only GET for the last handshake canary verdict.
 * Pass `enabled=false` for lecturer-driven 1:1 slots (no AI Examiner).
 */
export async function fetchHandshakeCanaryStatus(options?: { refresh?: boolean }): Promise<HandshakeCanaryStatus> {
  const response = await axios.get<{ data: HandshakeCanaryStatus & { hop?: string } }>(HANDSHAKE_CANARY_PATH, {
    params: options?.refresh ? { refresh: true } : undefined,
  });
  return mapHandshakeCanaryPayload(response.data?.data);
}

export function useHandshakeCanaryStatus(enabled = true) {
  const queryClient = useQueryClient();
  const lab = useOralExamLabOverrides();
  const labExaminerPresent = lab?.examinerPresent === true;
  const queryEnabled = enabled && !labExaminerPresent;

  const query = useQuery({
    queryKey: getHandshakeCanaryQueryKey(),
    queryFn: () => fetchHandshakeCanaryStatus(),
    enabled: queryEnabled,
    staleTime: 0,
    retry: false,
    refetchInterval: (q) => getHandshakeCanaryRefetchIntervalMs(
      q.state.data as HandshakeCanaryStatus | undefined,
      {
        enabled: queryEnabled,
        isError: q.state.status === 'error',
        isFetched: q.state.fetchStatus !== 'fetching' && q.state.dataUpdateCount > 0,
      },
    ),
  });

  const refetchWithRefresh = useCallback(async () => {
    if (labExaminerPresent) {
      const data = labHealthyHandshakeCanaryStatus();
      queryClient.setQueryData(getHandshakeCanaryQueryKey(), data);
      return data;
    }
    const data = await fetchHandshakeCanaryStatus({ refresh: true });
    queryClient.setQueryData(getHandshakeCanaryQueryKey(), data);
    return data;
  }, [labExaminerPresent, queryClient]);

  if (labExaminerPresent) {
    const data = labHealthyHandshakeCanaryStatus();
    return {
      ...query,
      data,
      isFetched: true,
      isError: false,
      isLoading: false,
      isSuccess: true,
      refetchWithRefresh,
    };
  }

  return {
    ...query,
    refetchWithRefresh,
  };
}
